import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTour } from './TourProvider';
import Modal from './Modal';
import { ConfettiBurst } from './MilestoneCelebration';
import BadgeService from '../../services/badgeService';
import { BADGE_UNLOCK_CHECK_EVENT } from '../../utils/badgeUnlockEvents';

/**
 * BadgeUnlockCelebration — the full-screen moment when a badge is earned.
 *
 * Mounted once in App. Two triggers:
 *   1. A `lyf:check-badge-unlocks` window event, dispatched (after a short
 *      delay) by completion flows via requestBadgeUnlockCheck().
 *   2. An initial check shortly after sign-in/app load — this is how badges
 *      awarded out-of-band (auto-completed trips, backfills, awards earned
 *      on another device) still get their moment.
 *
 * Badges queue and celebrate one at a time; each is marked notified on
 * dismiss so it never shows again.
 */
function BadgeUnlockCelebration() {
  const { t } = useTranslation('onboarding');
  const { currentUser: user, termsOutdated } = useAuth();
  const { isActive: isTourActive } = useTour();
  const [queue, setQueue] = useState([]);
  const seenIdsRef = useRef(new Set());

  const checkForUnlocks = useCallback(async () => {
    if (!user?.id) return;
    try {
      const result = await BadgeService.getUnnotifiedBadges(user.id);
      if (!result.success || !result.badges?.length) return;
      // Dedupe OUTSIDE the state updater — updaters must stay pure (React
      // StrictMode double-invokes them; mutating the ref inside would filter
      // everything out on the second pass and drop the celebration)
      const fresh = result.badges.filter(b => !seenIdsRef.current.has(b.id));
      if (!fresh.length) return;
      fresh.forEach(b => seenIdsRef.current.add(b.id));
      setQueue(prev => [...prev, ...fresh]);
    } catch {
      // Celebration is best-effort — never surface errors for it
    }
  }, [user?.id]);

  // Completion flows ask for a check via the window event
  useEffect(() => {
    window.addEventListener(BADGE_UNLOCK_CHECK_EVENT, checkForUnlocks);
    return () => window.removeEventListener(BADGE_UNLOCK_CHECK_EVENT, checkForUnlocks);
  }, [checkForUnlocks]);

  // Initial check after sign-in/app load
  useEffect(() => {
    if (!user?.id) return;
    const timer = setTimeout(checkForUnlocks, 2500);
    return () => clearTimeout(timer);
  }, [user?.id, checkForUnlocks]);

  const current = queue[0] || null;

  const dismiss = useCallback(() => {
    if (!current) return;
    BadgeService.markBadgeNotified(current.id).catch(() => {});
    setQueue(prev => prev.slice(1));
  }, [current]);

  // Hold the celebration while the ToS gate or a tour is up — otherwise the
  // badge modal renders visible-but-dead under the tour backdrop, or stacks
  // on the blocking terms dialog. The queue is kept; it shows once clear.
  if (!current || isTourActive || termsOutdated) return null;

  return (
    <>
      <ConfettiBurst key={current.id} />
      <Modal
        isOpen
        onClose={dismiss}
        size="sm"
        backdrop="blur"
        ariaLabel={t('badgeUnlock.ariaLabel', { name: current.name })}
      >
        <div className="px-6 pt-10 pb-8 text-center">
          {/* Badge medallion — same accent treatment as earned badges on the profile */}
          <div className="mx-auto mb-6 w-28 h-28 rounded-full bg-accent-light border border-accent/20 flex items-center justify-center">
            <span className="text-6xl animate-bounce motion-reduce:animate-none" aria-hidden="true">
              {current.emoji}
            </span>
          </div>

          <p className="text-xs font-semibold uppercase tracking-wide text-accent-dark mb-2">
            {t('badgeUnlock.label')}
          </p>
          <h2 className="text-3xl font-semibold text-content-primary mb-2">
            {current.name}
          </h2>
          <p className="text-content-secondary mb-8">
            {current.description}
          </p>

          <button
            onClick={dismiss}
            className="w-full py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
          >
            {t('badgeUnlock.dismissButton')}
          </button>

          {queue.length > 1 && (
            <p className="text-xs text-content-muted mt-3">
              {t('badgeUnlock.moreBadges', { count: queue.length - 1 })}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
}

export default BadgeUnlockCelebration;
